import React, { useContext, useEffect, useState } from 'react';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import Dialog from '../common/Dialog.redesign';
import LeaseGenerationOwnerData from 'src/models/leasing/LeaseGenerationOwnerData';
import useFetch from 'use-http';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import OwnerTracts from '../project/sections/owners/OwnerTracts';
import Project from 'src/models/Project';
import FormTextBox from 'src/components/controls/forms/redesign/FormTextbox';
import Form from 'src/components/controls/forms/Form';
import SearchResult from 'src/models/OwnerSearchResult';
import _ from 'lodash';
import Card from 'src/components/common/Card.redesign';
import CardContainer from '../common/CardContainer.redesign';
import OwnerContactNotes from '../project/sections/owners/OwnerContactNotes';
import OwnerNotes from '../project/sections/owners/OwnerNotes';
import Button from '../controls/forms/redesign/FormButton';
import RHFCheckbox from '../controls/tempRHFControls/RHFCheckBox';
import { useForm } from 'react-hook-form';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { ODataFilter, ODataFilterOperators, ODataOptions } from 'src/util/ODataBuilder';
import { ITableConfig } from '../common/table';

interface IMergeOwnersDialogProps {
    selectedOwnerLookupId: number;
    projectData: Project;
    onNavigate: (path: string) => void;
    onMerge: () => void;
    onCloseDialog?: () => void;
}

const MergeOwnersDialog: React.FC<IMergeOwnersDialogProps> = (props) => {
    console.log('Merge Props!', props);
    const appContext = useContext<IAppContextState>(AppContext);

    const { get, put, response } = useFetch();
    const { register, getValues, reset } = useForm();

    const [primaryOwner, setPrimaryOwner] = useState<LeaseGenerationOwnerData>(null);
    const [mergeOwner, setMergeOwner] = useState<LeaseGenerationOwnerData>(null);
    const [mergeOwnerResults, setMergeOwnerResults] = useState<PagedResultSet<SearchResult>>(null);
    const [mergeNotes, setMergeNotes] = useState<boolean>(true);
    const [currentSearchText, setCurrentSearchText] = useState<string>('');
    const [isOnLastPage, setIsOnLastPage] = useState<boolean>(false);
    const [mergeListConfig, setMergeListConfig] = useState<ITableConfig>({ rowCount: 15, pageNumber: 1});

    useEffect(() => {
        getOwnerDetail(props.selectedOwnerLookupId, true);
        let mergeDetail = { includeOwnerNotes: true };
        reset(mergeDetail);
    }, []);

    useEffect(() => {
        if (primaryOwner !== null) {
            console.log('Primary Owner!', primaryOwner);
        }
    }, [primaryOwner]);

    useEffect(() => {
        let lastPage = false;
        if (mergeOwnerResults) {
            lastPage = mergeOwnerResults.pageNumber * mergeOwnerResults.pageSize < mergeOwnerResults.totalRecords;
        }
        setIsOnLastPage(lastPage);
    }, [mergeOwnerResults]);

    const getOwnerDetail = async ( ownerId: number, forPrimary: boolean) => {
        const result: LeaseGenerationOwnerData = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leasegeneration/owner/${ownerId}`
        );

        if (response.ok) {
            if (forPrimary) {
                setPrimaryOwner(result);
            } else {
                setMergeOwner(result);
            }
        }
    };

    const getOwnerSearchResults = async (options: ODataOptions) => {
        // return this.getRequest(`${this.baseUrl}v1.0/clients/${clientNumber}/projects/${projectNumber}/owner-search/${searchText}/${ownerType}`, jwt);
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owner-search${options.toString()}`);

        if (response.ok) {
            console.log('Search results:', result);
            setMergeOwnerResults(result);
        }
    };

    const getMergeData = (ownerId: number) => {
        if (ownerId > 0) {
            getOwnerDetail(ownerId, false);
        }
    };

    const displayOwnerSearchResults = () => {
        if (mergeOwnerResults?.items?.length > 0) {
            console.log('Primary Owner:', primaryOwner);
            console.log('Merge Results', mergeOwnerResults);
            return mergeOwnerResults.items.filter((mo: SearchResult) => mo.id !== primaryOwner?.ownerDetails?.ownerLookUpId).map((owner: SearchResult) => {
                return (
                    <Card
                        key={`owner-${owner.id}`}
                        onClick={() => getMergeData(owner.id)}
                        badge={`Owner #${owner.id}`}
                        className={owner.id === mergeOwner?.id ? 'selected' : ''}
                        style={{ marginTop: '1rem' }}
                    >
                        <div style={{ padding: '1rem' }}>
                            <div className="title">{owner.name}</div>
                            <div>{owner.type}</div>
                        </div>
                    </Card>
                );
            });
        } else {
            return <div>No Owners found</div>;
        }
    };

    const searchUpdate = _.debounce((searchText: string) => {
        setCurrentSearchText(searchText);
        const options = ODataOptions.from(mergeListConfig);
        if (searchText === undefined || searchText === '') {
            searchText = '_';
        } else {
            options.addFilter(new ODataFilter('omni', ODataFilterOperators.Contains, searchText));
        }
        
        getOwnerSearchResults(options);
    }, 300);

    const handleMerge = async () => {
        console.log('Items to be merged:', primaryOwner.ownerDetails.ownerLookUpId, mergeOwner.ownerDetails.ownerLookUpId, mergeNotes);
        // tslint:disable-next-line: max-line-length
        let mergeUrl = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owners/merge-owners/${primaryOwner.ownerDetails.ownerLookUpId}/${mergeOwner.ownerDetails.ownerLookUpId}/${mergeNotes}`;

        await put(mergeUrl);

        if (response.ok) {
            props.onMerge();
            props.onCloseDialog();
        }
    };

    const handleCheckBoxChange = () => {
        let ref = getValues();
        setMergeNotes(ref.includeOwnerNotes);
    };

    const incrementOwners = () => {
        let newConfig = mergeListConfig;
        newConfig.pageNumber = mergeListConfig.pageNumber + 1;
        setMergeListConfig(newConfig);

        const options = ODataOptions.from(newConfig);
        if (currentSearchText !== undefined || currentSearchText !== '') {
            setCurrentSearchText('_');
        }
        options.addFilter(new ODataFilter('omni', ODataFilterOperators.Contains, currentSearchText));

        getOwnerSearchResults(options);
    };

    return (
        <Dialog title="Merge Owners">
            <div style={{ display: 'flex', width: '60vw', height: '80vh', overflow: 'auto' }}>
                <Page style={{ display: 'flex', clear: 'both', width: '100%' }}>
                    <Page.Section>
                        {!!primaryOwner && (
                            <>
                                <Panel>
                                    <CardContainer
                                        showNextPagePanel={isOnLastPage}
                                        nextPageAction={incrementOwners}
                                    >
                                        <Card
                                            key={`owner-${primaryOwner?.ownerDetails?.ownerLookUpId}`}
                                            badge={`Owner #${primaryOwner?.ownerDetails?.ownerLookUpId}`}
                                            className="selected"
                                            style={{ marginTop: '1rem' }}
                                        >
                                            <div style={{ padding: '1rem' }}>
                                                <div className="title">{primaryOwner?.ownerDetails?.name}</div>
                                                <div>{primaryOwner?.ownerDetails?.ownerType}</div>
                                            </div>
                                        </Card>
                                    </CardContainer>
                                </Panel>
                                <Panel>
                                    <div style={{ marginLeft: '1rem', width: '95%' }} className="dlc-content-panel-header">
                                        Tracts List
                                    </div>
                                    <OwnerTracts
                                        tracts={primaryOwner && primaryOwner.tracts}
                                        clientNumber={appContext.clientNumber}
                                        projectNumber={appContext.projectNumber}
                                        navigate={props.onNavigate}
                                        projectData={props.projectData}
                                        maxHeight="20vh"
                                        readonly={true}
                                    />
                                </Panel>
                                <Panel fitPanelToScreen={true}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                        <div style={{ marginLeft: '1rem', width: '75%' }} className="dlc-content-panel-header">
                                            Contact Notes
                                        </div>
                                    </div>

                                    <OwnerContactNotes
                                        contactNotes={primaryOwner.contactNotes}
                                        ownerLookUpId={primaryOwner?.ownerDetails?.ownerLookUpId}
                                        onEditContactNote={undefined}
                                        ownerData={primaryOwner}
                                        isReadonly={true}
                                    />
                                </Panel>
                                <Panel fitPanelToScreen={true}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                        <div
                                            style={
                                                primaryOwner?.ownerDetails?.note
                                                    ? { marginLeft: '1rem', width: '95%' }
                                                    : { marginLeft: '1rem', width: '75%' }
                                            }
                                            className="dlc-content-panel-header"
                                        >
                                            Owner Note
                                        </div>
                                    </div>
                                    <OwnerNotes
                                        ownerData={primaryOwner}
                                        ownerLookUpId={primaryOwner?.ownerDetails?.ownerLookUpId}
                                        refreshContainer={undefined}
                                        showDialog={undefined}
                                        hideDialog={undefined}
                                        isReadOnly={true}
                                    />
                                </Panel>
                            </>
                        )}
                    </Page.Section>
                    <Page.Section>
                        {!mergeOwner && (
                            <Panel>
                                <div>Select Owner to Merge</div>
                                <Form onSubmit={() => console.log('form submitted')}>
                                    <FormTextBox
                                        name="searchText"
                                        label="Owner Search by Name"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => searchUpdate(e.target.value)}
                                    />
                                </Form>
                                <div className="dlc-content-panel-header">Owners</div>
                                <CardContainer style={{ overflow: 'auto', maxHeight: '68vh' }}>{displayOwnerSearchResults()}</CardContainer>                            
                            </Panel>
                        )}
                        {!!mergeOwner && (
                            <>
                                <Panel>
                                    <CardContainer>
                                        <Card
                                            key={`owner-${mergeOwner.ownerDetails?.ownerLookUpId}`}
                                            badge={`Owner #${mergeOwner.ownerDetails?.ownerLookUpId}`}
                                            className="selected"
                                            style={{ marginTop: '1rem' }}
                                        >
                                            <div style={{ padding: '1rem', width: '90%' }}>
                                                <div className="title">{mergeOwner.ownerDetails?.name}</div>
                                                <div>{mergeOwner.ownerDetails?.ownerType}</div>
                                            </div>
                                            <div className="dlc-dialog-close" onClick={() => setMergeOwner(null)}>
                                                <span className="dlc-dialog-close-icon">
                                                    <i className="fas fa-times" />
                                                </span>
                                            </div>
                                        </Card>
                                    </CardContainer>
                                </Panel>
                                <Panel>
                                    <div style={{ marginLeft: '1rem', width: '95%' }} className="dlc-content-panel-header">
                                        Tracts List
                                    </div>
                                    <OwnerTracts
                                        tracts={mergeOwner && mergeOwner.tracts}
                                        clientNumber={appContext.clientNumber}
                                        projectNumber={appContext.projectNumber}
                                        navigate={props.onNavigate}
                                        projectData={props.projectData}
                                        maxHeight="20vh"
                                        readonly={true}
                                    />
                                </Panel>
                                <Panel fitPanelToScreen={true}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                        <div style={{ marginLeft: '1rem', width: '75%' }} className="dlc-content-panel-header">
                                            Contact Notes
                                        </div>
                                    </div>

                                    <OwnerContactNotes
                                        contactNotes={mergeOwner.contactNotes}
                                        ownerLookUpId={mergeOwner?.ownerDetails?.ownerLookUpId}
                                        onEditContactNote={undefined}
                                        ownerData={mergeOwner}
                                        isReadonly={true}
                                    />
                                </Panel>
                                <Panel fitPanelToScreen={true}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                        <div
                                            style={
                                                mergeOwner?.ownerDetails?.note
                                                    ? { marginLeft: '1rem', width: '95%' }
                                                    : { marginLeft: '1rem', width: '75%' }
                                            }
                                            className="dlc-content-panel-header"
                                        >
                                            Owner Note
                                        </div>
                                    </div>
                                    <OwnerNotes
                                        ownerData={mergeOwner}
                                        ownerLookUpId={mergeOwner?.ownerDetails?.ownerLookUpId}
                                        refreshContainer={undefined}
                                        showDialog={undefined}
                                        hideDialog={undefined}
                                        isReadOnly={true}
                                    />
                                </Panel>
                                <div>
                                    <form>
                                        <RHFCheckbox name="includeOwnerNotes" label="Merge Owner Notes?" onClick={handleCheckBoxChange} register={register} />
                                    </form>
                                    <Button text="Merge Owners" onClick={() => handleMerge()} />
                                </div>
                            </>
                        )}
                    </Page.Section>
                </Page>
            </div>
        </Dialog>
    );
};

export default MergeOwnersDialog;


// WEBPACK FOOTER //
// ./src/components/dialogs/MergeOwnersDialog.tsx